/**
 * ListPageShell — 列表頁通用外殼（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 整合「PageShell + 工具列（搜尋/篩選）+ 列表容器」的高階組件。
 * 業務頁面只需傳入 filters / actions / children（通常是 Table），
 * 不再需要重複實現 page-header + filter-bar + 容器包裹。
 *
 * 升級亮點：
 *   - filter 區：surface-muted 背景 + 1px border，比純白更有層次感
 *   - 容器：1px border subtle，radius = md，shadow = xs
 *   - toolbar 行：左右分隔明確
 *
 * 慣用排版：
 *   ┌── header（title / description / actions）──────────────┐
 *   │  filters bar（石灰底 + ring，搜尋 + 下拉篩選 + 重置）  │
 *   ├──────────────────────────────────────────────────┤
 *   │  toolbar（左側：批量操作 / 右側：視圖切換）            │
 *   ├──────────────────────────────────────────────────┤
 *   │  table 內容區                                      │
 *   └──────────────────────────────────────────────────┘
 */
import type { ReactNode } from 'react';
import PageShell from '@/components/shell/PageShell';
import { color, radius, shadow, space } from '@/styles/tokens';

export interface ListPageShellProps {
  /** Page header 區 */
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  /** 右上主要操作按鈕（如「新建」） */
  actions?: ReactNode;
  /** 篩選條：搜尋框 / 下拉篩選 / 日期範圍等（FilterBar 或自定義） */
  filters?: ReactNode;
  /** 工具列：批量操作 / 視圖切換 / 列管理 */
  toolbar?: ReactNode;
  /** 表格內容（或自定義列表） */
  children: ReactNode;
  /** 是否將表格直接貼合容器（無內邊距），預設 true */
  flushTable?: boolean;
}

export default function ListPageShell({
  title,
  description,
  meta,
  actions,
  filters,
  toolbar,
  children,
  flushTable = true,
}: ListPageShellProps) {
  return (
    <PageShell title={title} description={description} meta={meta} actions={actions}>
      <div
        style={{
          background: color.surface.base,
          border: `1px solid ${color.border.subtle}`,
          borderRadius: radius.md,
          boxShadow: shadow.xs,
          overflow: 'hidden',
        }}
      >
        {/* 篩選條區：帶背景色和 ring，與表格內容明確分層 */}
        {filters && (
          <div
            style={{
              padding: `${space[3]}px ${space[5]}px`,
              borderBottom: `1px solid ${color.border.subtle}`,
              background: color.surface.muted,
            }}
          >
            {filters}
          </div>
        )}

        {/* 工具列：左右分隔（批量操作 / 列管理） */}
        {toolbar && (
          <div
            style={{
              padding: `10px ${space[5]}px`,
              borderBottom: `1px solid ${color.border.subtle}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: space[3],
              background: color.surface.base,
            }}
          >
            {toolbar}
          </div>
        )}

        {/* 主內容（Table 或自定義列表） */}
        <div style={{ padding: flushTable ? 0 : space[5] }}>{children}</div>
      </div>
    </PageShell>
  );
}
